import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useLocation,
  matchPath,
} from "react-router-dom";
import AuthWrapper from "../views/Auth/AuthWrapper";
import HandoffPage from "../views/Auth/HandoffPage";
import ScrollToTop from "../shared/components/ScrollToTop";
import AppHeader from "../shared/components/AppHeader";
import AppSidebar from "../shared/components/AppSidebar";
import { AppRoutes } from "./routeConstants/appRoutes";
import ScanCentreList from "../views/ScanCenters/ScanCenterList";
import DoctorList from "../views/Doctors/DoctorList";
import ReferringDoctorList from "../views/ReferringDoctors/ReferringDoctorList";
import IssueList from "../views/Issues/IssueList";
import requireAuth from "../shared/components/HOC/requireAuth";
import AuthContainer from "../store/container/AuthContainer";
import { AuthReducerProps } from "../store/reducers/authReducer";
import AuthService from "../services/AuthService/auth.service";
import AppLoader from "../shared/components/AppLoader";
import { UserRoleEnum } from "../enums/userRole.enum";
import AdminCases from "../views/Cases/AdminCases";
import ScanCenterCases from "../views/Cases/ScanCenterCases";
import DoctorCases from "../views/Cases/DoctorCases";
import RestrictAccess from "../shared/components/RestrictedAccess";
import DoctorSummary from "../views/Doctors/DoctorSummary";
import ScanCenterSummary from "../views/ScanCenters/ScanCenterSummary";
import ReportTemplateList from "../views/ReportTemplates/ReportTemplateList";
import AdminProfile from "../views/Profile/AdminProfile";
import ScanCenterProfile from "../views/Profile/ScanCenterProfile";
import DoctorProfile from "../views/Profile/DoctorProfile";
import { AdminTypeEnum } from "../enums/AdminType.enum";
import ViewerWrapper from "../shared/components/ViewerWrapper";
import ViewerWrapperGuest from "../shared/components/GuestViewerWrapper";
import SharedPageRedirect from "../views/SharedPage/SharedPageRedirect";
import CustomTagsList from "../views/CustomTags/CustomTagsList";
import ReportMacrosList from "../views/ReportMacros/ReportMacrosList";
import GenerateReportPage from "../views/Cases/GenerateReport";
import OnlyOfficePage from "../shared/components/OnlyOfficePage/OnlyOfficePage";
import ReleaseNotes from "../views/ReleaseNotes";
import OpenChatFromQuery from "../shared/components/OpenChatFromQuery";
// Pre-wrap static route components with requireAuth at module level.
// This is critical: calling requireAuth() inside render creates a new component
// type on every render, causing React to unmount + remount the route component
// and re-fire all its useEffect hooks (including API calls).
const AuthScanCentreList = requireAuth(ScanCentreList);
const AuthDoctorList = requireAuth(DoctorList);
const AuthReferringDoctorList = requireAuth(ReferringDoctorList);
const AuthCustomTagsList = requireAuth(CustomTagsList);
const AuthReportMacrosList = requireAuth(ReportMacrosList);
const AuthReleaseNotes = requireAuth(ReleaseNotes);
const AuthDoctorSummary = requireAuth(DoctorSummary);
const AuthScanCenterSummary = requireAuth(ScanCenterSummary);
const AuthIssueList = requireAuth(IssueList);
const AuthViewerWrapper = requireAuth(ViewerWrapper);
const AuthReportTemplateList = requireAuth(ReportTemplateList);
const AuthGenerateReportPage = requireAuth(GenerateReportPage);

// Pre-wrap all role-specific components so we can pick between stable references
const AuthAdminCases = requireAuth(AdminCases);
const AuthScanCenterCases = requireAuth(ScanCenterCases);
const AuthDoctorCases = requireAuth(DoctorCases);
const AuthRestrictAccess = requireAuth(RestrictAccess);
const AuthAdminProfile = requireAuth(AdminProfile);
const AuthScanCenterProfile = requireAuth(ScanCenterProfile);
const AuthDoctorProfile = requireAuth(DoctorProfile);
function useHideShell() {
  const location = useLocation();
  const isGenerateReport = Boolean(
    matchPath(location.pathname, { path: AppRoutes.GENERATE_REPORT, exact: true })
  );
  const isAuthRoute = Boolean(
    matchPath(location.pathname, { path: AppRoutes.AUTH, exact: false })
  );
  const isCaseViewerIframe = Boolean(
    matchPath(location.pathname, { path: AppRoutes.CASE_DETAIL, exact: true })
  );
  const isGuestCaseViewer = Boolean(
    matchPath(location.pathname, { path: AppRoutes.CASE_DETAIL_GUEST, exact: true })
  );
  return isGenerateReport || isAuthRoute || isCaseViewerIframe || isGuestCaseViewer;
}

function AppLayout({
  collapsed,
  setCollapsed,
  children,
}: {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  children: React.ReactNode;
}) {
  const hideShell = useHideShell();
  return (
    <>
      {!hideShell && (
        <>
          <AppHeader collapsed={collapsed} setCollapsed={setCollapsed} />
        </>
      )}
      <>
      <AppSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </>
      <ScrollToTop>
        <div className={hideShell ? "" : `app-wrapper ${collapsed ? "collapsed" : "expanded"}`}>
          {children}
        </div>
      </ScrollToTop>
    </>
  );
}

interface AppRouterProps extends AuthReducerProps {}
function AppRouter({
  authenticated,
  userRole,
  setUserRole,
}: AppRouterProps) {
  const [showRoutes, setShowRoutes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(window.innerWidth < 2000); 

  // Derive role-based components only when userRole changes
  const AuthHomeComponent = useMemo(() => {
    if (userRole === UserRoleEnum.ADMIN) return AuthAdminCases;
    if (userRole === UserRoleEnum.SCAN_CENTER) return AuthScanCenterCases;
    if (userRole === UserRoleEnum.DOCTOR) return AuthDoctorCases;
    return AuthRestrictAccess;
  }, [userRole]);

  const AuthProfileComponent = useMemo(() => {
    if (userRole === UserRoleEnum.ADMIN) return AuthAdminProfile;
    if (userRole === UserRoleEnum.SCAN_CENTER) return AuthScanCenterProfile;
    if (userRole === UserRoleEnum.DOCTOR) return AuthDoctorProfile;
    return AuthRestrictAccess;
  }, [userRole]);

  useEffect(() => {
    if (!authenticated) {
      setShowRoutes(true);
    } else {
      if (!userRole) {
        if (localStorage.getItem("auth")) {
          const auth = JSON.parse(localStorage.getItem("auth") || "");
          setLoading(true);
          AuthService.getUserRole(
            auth.uid,
            (userRole: string, adminType?: AdminTypeEnum) => {
              setShowRoutes(true);
              setUserRole(userRole, adminType);
            },
            () => {},
            () => {
              setLoading(false);
            }
          );
        }
      }
    }
  }, [authenticated, userRole]);

  return (
    <div className="app-routes" id="appRoutes">
      {showRoutes && (
        <Router>
          <AppLayout collapsed={collapsed} setCollapsed={setCollapsed}>
            <Switch>
              {/* Cross-origin Classic/New handoff. Must come before the
                  catch-all `/auth` route so AuthWrapper doesn't swallow it. */}
              <Route exact path={AppRoutes.AUTH_HANDOFF} component={HandoffPage} />
              <Route exact={false} path={AppRoutes.AUTH} component={AuthWrapper} />
              <Route exact={false} path={AppRoutes.SHARED_PAGE} component={SharedPageRedirect} />
              <Route exact={true} path={AppRoutes.CASE_DETAIL_GUEST} component={ViewerWrapperGuest} />
              <Route exact path={AppRoutes.HOME} component={AuthHomeComponent} />
              <Route exact path={AppRoutes.SCAN_CENTRES} component={AuthScanCentreList} />
              <Route exact path={AppRoutes.DOCTORS} component={AuthDoctorList} />
              <Route exact path={AppRoutes.REFERRING_DOCTORS} component={AuthReferringDoctorList} />
              <Route exact path={AppRoutes.CUSTOM_TAGS} component={AuthCustomTagsList} />
              <Route exact path={AppRoutes.DOCTOR_SUMMARY} component={AuthDoctorSummary} />
              <Route exact path={AppRoutes.SCAN_CENTER_SUMMARY} component={AuthScanCenterSummary} />
                  {/* To be used for future use */}
                  {/* <Route exact path={AppRoutes.ADMINS} component={requireAuth(AdminList)} /> */}
                  {/* <Route exact path={AppRoutes.ISSUES} component={AuthIssueList} /> */}
              <Route exact path={AppRoutes.CASE_DETAIL} component={AuthViewerWrapper} />
              <Route exact path={AppRoutes.GENERATE_REPORT} component={AuthGenerateReportPage} />
              <Route exact path={AppRoutes.REPORT_TEMPLATES} component={AuthReportTemplateList} />
              <Route exact path={AppRoutes.REPORT_MACROS} component={AuthReportMacrosList} />
              <Route exact path={AppRoutes.PROFILE} component={AuthProfileComponent} />
              <Route exact path={AppRoutes.RELEASE_NOTES} component={AuthReleaseNotes} />
              <Route exact={true} path={AppRoutes.GENERATE_ONLYOFFICE_REPORT} component={OnlyOfficePage} />
            </Switch>
          </AppLayout>
        </Router>
      )}
      <AppLoader loading={loading} />
    </div>
  );
}

export default AuthContainer(AppRouter);
